// Campaign.js
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FaCoins, FaPlus, FaToggleOff, FaToggleOn, FaTrash, FaCopy } from "react-icons/fa"; // Added FaCopy
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoaderSpiner from "../LoaderSpiner";
import { truncateText } from "./utils.js";

import styles from "./styles/Campaign.module.css";

const formatDate = (date, includeTime = false) => {
  if (!date || isNaN(new Date(date))) {
    return "N/A";
  }
  const dateOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  };
  const timeOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  const formattedDate = new Date(date).toLocaleDateString("en-US", dateOptions);
  if (includeTime) {
    const formattedTime = new Date(date).toLocaleTimeString(
      "en-US",
      timeOptions
    );
    return `${formattedDate} - ${formattedTime}`;
  }
  return formattedDate;
};

const DataTable = memo(({ viewData }) => {
  const renderCellValue = (value) => {
    if (value === null || value === undefined) {
      return "N/A";
    }
    if (Array.isArray(value)) {
      return truncateText(value.join(", "), 50);
    }
    if (typeof value === "object") {
      return truncateText(JSON.stringify(value), 50);
    }
    return truncateText(String(value), 50);
  };

  const cleanColumnName = (key) => {
    return key
      .replace(/^other\.\d+other\./i, "")
      .replace(/^other\./i, "")
      .replace(/^\d+\./, "")
      .replace(/^\.+/, "")
      .trim();
  };

  const flattenObject = (obj, prefix = "") => {
    const flattened = {};
    for (const key in obj) {
      if (key === "_id") continue;
      const value = obj[key];
      if (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        Object.assign(flattened, flattenObject(value, prefix));
      } else {
        const cleanKey = cleanColumnName(key);
        if (cleanKey) {
          flattened[cleanKey] = value;
        }
      }
    }
    return flattened;
  };

  const flattenedData = viewData.map((row) => flattenObject(row));

  const allKeys = [
    ...new Set(flattenedData.flatMap((row) => Object.keys(row))),
  ];
  console.log("allkeys", allKeys);

  function formatDate(dateString) {
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";

    hours = hours % 12 || 12; // convert 0 to 12 for 12-hour format

    return `${year}-${month}-${day} ${hours}.${minutes}${ampm}`;
  }

  const normalizedData = flattenedData.map((row) => {
    const normalizedRow = {};
    allKeys.forEach((key) => {
      let value = row[key] ?? null;

      if (key === "createdAt" && value) {
        value = formatDate(value);
      }

      normalizedRow[key] = value;
    });
    return normalizedRow;
  });

  console.log("normalizedData", normalizedData);

  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <table className={styles.dataTable}>
      <thead>
        <tr>
          <th>Serial Number</th>
          {allKeys
            .filter((key) => key !== "0")
            .map((key, index) => (
              <th key={index}>{capitalizeFirstLetter(key)}</th>
            ))}
        </tr>
      </thead>
      <tbody>
        {normalizedData.map((row, rowIndex) => (
          <tr key={rowIndex}>
            <td>{rowIndex + 1}</td>
            {allKeys
              .filter((key) => key !== "0")
              .map((key, colIndex) => (
                <td key={colIndex}>{row[key]}</td>
              ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
});

const Campaign = () => {
  const [developerId, setDeveloperId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [files, setFiles] = useState([]);
  const [campaignName, setCampaignName] = useState("");
  const [type, setType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(""); // Fixed typo: setEndDateed to setEndDate
  const [targetSegment, setTargetSegment] = useState("");
  const [budget, setBudget] = useState("");
  const [developers, setDevelopers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [participantMap, setParticipantMap] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDataModalOpen, setIsDataModalOpen] = useState(false);
  const [isAssignCreditModalOpen, setIsAssignCreditModalOpen] = useState(false);
  const [assignCreditCampaignId, setAssignCreditCampaignId] = useState("");
  const [assignCreditAmount, setAssignCreditAmount] = useState("");
  const [assignCreditNotes, setAssignCreditNotes] = useState("");
  const [viewData, setViewData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [viewType, setViewType] = useState("card");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("role");
  const [spend, setSpend] = useState("");
  const [roi, setRoi] = useState("");
  const [initialCredit, setInitialCredit] = useState("");
  const [leadGenerated, setLeadGenerated] = useState("");
  const [error, setError] = useState("");
  const [isMetaModalOpen, setIsMetaModalOpen] = useState(false);
  const [metaCampaigns, setMetaCampaigns] = useState([]);
  const [selectedMetaCampaignId, setSelectedMetaCampaignId] = useState("");
  const [isWebModalOpen, setIsWebModalOpen] = useState(false);
  const [generatedApi, setGeneratedApi] = useState("");
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [isApiModalOpen, setIsApiModalOpen] = useState(false);

  const navigate = useNavigate();

  const toastOptions = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
  };

  // Fetch developers
  useEffect(() => {
    const fetchDevelopers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `https://prod.toxboxai.com/api/developer/getalldevelopers?_t=${Date.now()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          setDevelopers(response.data.data.developers || []);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    if (userRole === "vendor") {
      fetchDevelopers();
    }
  }, [token, userRole, navigate]);
  const fetchMetaCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://prod.toxboxai.com/api/campaign/metaCampaign`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setMetaCampaigns(res.data.data.campaigns || []);
      }
    } catch (e) {
      toast.error("Failed to load Meta campaigns", toastOptions);
    } finally {
      setLoading(false);
    }
  }, [token]);
  // Fetch campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const url =
          userRole === "vendor"
            ? `https://prod.toxboxai.com/api/campaign/getallcampaign?_t=${Date.now()}`
            : `https://prod.toxboxai.com/api/campaign/assigned-campaigns?_t=${Date.now()}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          const normalizedCampaigns = (response.data.data.campaigns || []).map(
            (campaign) => ({
              ...campaign,
              campaignname: campaign.campaignname || "N/A",
              type: campaign.type || "N/A",
              startDate: campaign.startDate || null,
              endDate: campaign.endDate || null,
              targetSegment: campaign.targetSegment || "N/A",
              budget: campaign.budget || 0,
              developerId: campaign.developerId || "N/A",
              spend: campaign.spend || 0,
              roi: campaign.roi || 0,
              initialCredit: campaign.creditBalance || 0,
              leadGenerated: campaign.leadGenerated || 0,
              projectId: campaign.projectId || {
                projectname: "N/A",
                _id: null,
              },
              createdBy: campaign.createdBy || { email: "N/A" },
              campaignCurrentStatus:
                campaign.campaignCurrentStatus || "enabled",
              assignedParticipants: (campaign.assignedParticipants || []).map(
                (participant) => ({
                  _id: participant._id || participant,
                  name:
                    participantMap[participant._id || participant] || "Unknown",
                })
              ),
            })
          );
          setCampaigns(normalizedCampaigns);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [token, userRole, refreshTrigger, navigate]);

  // Fetch participant names only when necessary
  const fetchParticipantNames = useCallback(async (participantIds) => {
    if (!participantIds.length) return;

    const newParticipantIds = participantIds.filter(
      (id) => !participantMap[id]
    );
    if (!newParticipantIds.length) return;

    setLoading(true);
    try {
      const response = await axios.get(
        "https://prod.toxboxai.com/api/vendor/participants",
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const newParticipantMap = response.data.data.reduce(
          (map, participant) => {
            map[participant._id] = participant.name || "Unknown";
            return map;
          },
          { ...participantMap }
        );
        setParticipantMap(newParticipantMap);
      }
    } catch (err) {
      console.error("Failed to fetch participant names:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!campaigns.length) return;

    const participantIds = [
      ...new Set(
        campaigns.flatMap((campaign) =>
          campaign.assignedParticipants.map((p) => p._id)
        )
      ),
    ];
    fetchParticipantNames(participantIds);
  }, [campaigns, fetchParticipantNames]);

  // Memoize filtered campaigns to prevent unnecessary re-renders
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) =>
      campaign.campaignname.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [campaigns, searchQuery]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) {
      setFiles([]);
      return;
    }
    if (selectedFiles.length > 10) {
      setError("Cannot upload more than 10 files.");
      setFiles([]);
      return;
    }
    for (const file of selectedFiles) {
      if (!file.name.match(/\.xlsx$/)) {
        setError(
          `File ${file.name} is not a .xlsx file. Please select .xlsx files only.`
        );
        setFiles([]);
        return;
      }
    }
    setFiles(selectedFiles);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      setError("User ID not found. Please log in again.");
      return;
    }

    if (files.length === 0) {
      setError("Please select at least one .xlsx file.");
      return;
    }

    const formattedCampaignName = startDate
      ? ` ${formatDate(startDate, true)} - ${campaignName}`
      : campaignName;

    const formData = new FormData();
    formData.append("campaignname", formattedCampaignName);
    formData.append("type", type);
    formData.append(
      "startDate",
      startDate ? new Date(startDate).toISOString() : ""
    );
    formData.append("endDate", endDate ? new Date(endDate).toISOString() : "");
    formData.append("targetSegment", targetSegment);
    formData.append("developerId", developerId);
    formData.append("projectId", projectId);
    files.forEach((file) => {
      formData.append("files", file);
    });

    setLoading(true);
    try {
      const response = await axios.post(
        "https://prod.toxboxai.com/api/campaign/createcampaign",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success("Campaign created successfully!", toastOptions);
        setCampaignName("");
        setType("");
        setStartDate("");
        setEndDate("");
        setTargetSegment("");
        setDeveloperId("");
        setProjectId("");
        setFiles([]);
        setError("");
        setIsModalOpen(false);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      } else {
        setError(
          "Failed to create campaign: " +
          (err.response?.data?.message || err.message)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  //webbase handle submit
  const handleWebBasedSubmit = async (e) => {
    e.preventDefault();

    if (
      !developerId ||
      !projectId ||
      !campaignName ||
      !startDate ||
      !endDate ||
      !targetSegment
    ) {
      toast.error("Please fill all required fields", toastOptions);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        developerId,
        projectId,
        campaignname: campaignName,
        type: type,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        targetSegment,
        initialCredit: Number(initialCredit) || 0,
      };

      const res = await axios.post(
        "https://prod.toxboxai.com/api/campaign/create-web-based-campaign",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data) {
        toast.success("Web-based campaign created successfully!", toastOptions);
        setIsWebModalOpen(false);
        setCampaignName("");
        setStartDate("");
        setEndDate("");
        setTargetSegment("");
        setDeveloperId("");
        setProjectId("");
        setInitialCredit("");
        setRefreshTrigger((p) => p + 1);
      }

      const campaignId = res.data?.data?.campaign?._id;
      const clientId = localStorage.getItem("userId"); // assuming you store vendor id in user state

      //  Generate dynamic curl example
      //       const apiCurl = `curl --location 'https://prod.toxboxai.com/api/lead/external-lead' \\
      // --header 'Content-Type: application/json' \\
      // --data '{
      //     "campaignId": "${campaignId}",
      //     "clientId": "${clientId}",
      //     "body": {
      //         "name": "John Doe",
      //         "phoneNumber": "9876543210",
      //         "purpose": "Demo",
      //         "message": "Interested in your service"
      //     }
      // }'`;

      const apiCurl = `
function prepareRequestContext(req) {
  return {
    "Content-Type": "application/json",
    "User-Agent": req.get("User-Agent") || req.headers["user-agent"] || "",
    "Referer": req.get("Referer") || req.headers["referer"] || "",
    "Origin": req.get("Origin") || req.headers["origin"] || "",
    "X-Forwarded-For": req.headers["x-forwarded-for"] || req.ip || ""
  };
}

const requestContext = prepareRequestContext(req);

curl --location 'https://prod.toxboxai.com/api/lead/external-lead' \\
--header "Content-Type: application/json" \\
--header "User-Agent: \${requestContext['User-Agent']}" \\
--header "Referer: \${requestContext['Referer']}" \\
--header "Origin: \${requestContext['Origin']}" \\
--header "X-Forwarded-For: \${requestContext['X-Forwarded-For']}" \\
--data '{
  "campaignId": "${campaignId}",
  "clientId": "${clientId}",
  "body": {
      "name": "John Doe",
      "phoneNumber": "9876543210",
      "purpose": "Demo",
      "message": "Interested in your service"
  }
}'`;


      setGeneratedApi(apiCurl);
      setIsApiModalOpen(true); // show API modal now
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.error || "Failed to create Web-based campaign",
        toastOptions
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCreditSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      return;
    }

    if (!assignCreditCampaignId) {
      toast.error("Please select a campaign.", toastOptions);
      return;
    }

    const selectedCampaign = campaigns.find(
      (c) => c._id === assignCreditCampaignId
    );
    if (!selectedCampaign) {
      toast.error("Selected campaign not found.", toastOptions);
      return;
    }

    const totalContacts = selectedCampaign?.sheetData?.length || 0;
    const currentCreditBalance =
      parseFloat(selectedCampaign?.creditBalance) || 0;
    const amount = parseFloat(assignCreditAmount) || 0;
    const totalCredits = amount + currentCreditBalance;

    if (!assignCreditAmount || amount <= 0) {
      toast.error("Please enter a valid amount greater than 0.", toastOptions);
      return;
    }

    if (totalCredits <= totalContacts) {
      setError(
        `Total credits (${totalCredits}) must be greater than total contacts (${totalContacts}).`
      );
      return;
    }

    const payload = {
      campaignId: assignCreditCampaignId,
      amount: amount.toString(),
      notes: assignCreditNotes || "",
    };

    setLoading(true);
    try {
      const response = await axios.post(
        "https://prod.toxboxai.com/api/campaign/assigncredit",
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Credit assigned successfully!", toastOptions);
        setAssignCreditCampaignId("");
        setAssignCreditAmount("");
        setAssignCreditNotes("");
        setIsAssignCreditModalOpen(false);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        toast.error(
          response.data.error || "Failed to assign credit",
          toastOptions
        );
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error(
          "Failed to assign credit: " +
          (err.response?.data?.error || err.message),
          toastOptions
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (campaign) => {
    if (!userId) {
      return;
    }

    const newStatus =
      campaign.campaignCurrentStatus === "enabled" ? "disabled" : "enabled";

    setLoading(true);
    try {
      const response = await axios.patch(
        `https://prod.toxboxai.com/api/campaign/update-status/${campaign._id}`,
        { campaignCurrentStatus: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Campaign ${newStatus} successfully!`, toastOptions);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error(
          "Failed to update campaign status: " +
          (err.response?.data?.error || err.message),
          toastOptions
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!userId) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(
        `https://prod.toxboxai.com/api/campaign/delete/${campaignId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Campaign deleted successfully!", toastOptions);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error(
          "Failed to delete campaign: " +
          (err.response?.data?.error || err.message),
          toastOptions
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewData = (campaign) => {
    setViewData(campaign.sheetData || []);
    setSelectedCampaign(campaign);
    setIsDataModalOpen(true);
  };

  const handleExport = () => {
    const csvHeader = [
      "CampaignName",
      "Type",
      "StartDate",
      "EndDate",
      "TargetSegment",
      "DeveloperId",
      "ProjectId",
      "AssignedParticipants",
      "Status",
    ].join(",");
    const csvRows = campaigns
      .map((campaign) =>
        [
          campaign.campaignname || "N/A",
          campaign.type || "N/A",
          formatDate(campaign.startDate),
          formatDate(campaign.endDate),
          campaign.targetSegment || "N/A",
          campaign.developerId || "N/A",
          campaign.projectId?.projectname || "N/A",
          campaign.assignedParticipants?.map((p) => p.name).join("; ") ||
          "Not assigned yet",
          campaign.campaignCurrentStatus || "enabled",
        ].join(",")
      )
      .join("\n");
    const csv = `${csvHeader}\n${csvRows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "campaigns_export.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Campaigns exported successfully!", toastOptions);
  };

  const modalVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -50 },
  };

  const handleMetaSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMetaCampaignId || !developerId || !projectId) {
      toast.error("Please fill all required fields", toastOptions);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        metaCampaignId: selectedMetaCampaignId,
        developerId,
        projectId,
        initialCredit: initialCredit || "0",
        autoStartSync: true,
      };

      const res = await axios.post(
        "https://prod.toxboxai.com/api/campaign/integrate-meta",
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success("Meta-integrated campaign created!", toastOptions);
        setIsMetaModalOpen(false);
        setRefreshTrigger((p) => p + 1);
        // reset fields
        setSelectedMetaCampaignId("");
        setDeveloperId("");
        setProjectId("");
        setInitialCredit("");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Failed to create Meta campaign",
        toastOptions
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignCredit = (campaignId) => {
    setAssignCreditCampaignId(campaignId);
    setIsAssignCreditModalOpen(true);
  };

  // New: Copy Campaign ID
  const handleCopyCampaignId = (campaignId) => {
    navigator.clipboard.writeText(campaignId);
    toast.success("Campaign ID copied to clipboard!", toastOptions);
  };

  return (
    <div className={styles.container}>
      <ToastContainer />
      <div className={styles.viewToggle}>
        <button
          className={`${viewType === "card" ? styles.activeView : ""} ${styles.cardbutton}`}
          onClick={() => setViewType("card")}
        >
          Card View
        </button>
        <button
          className={`${viewType === "table" ? styles.activeView : ""} ${styles.tablebutton
            }`}
          className={`${viewType === "table" ? styles.activeView : ""} ${styles.tablebutton}`}
          onClick={() => setViewType("table")}
        >
          Table View
        </button>
        <input
          className={styles.search}
          type="text"
          placeholder="Search campaigns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {userRole === "vendor" && (
          <div className={styles.createBtnGroup}>
            <button
              className={styles.createBtn}
              onClick={() => setIsCreateMenuOpen(!isCreateMenuOpen)}
            >
              <FaPlus /> Create Campaign
            </button>
            <button className={styles.createBtn} onClick={handleExport}>
              Export Campaigns
            </button>
            {isCreateMenuOpen && (
              <div className={styles.createMenu}>
                <button
                  className={styles.menuItem}
                  onClick={() => {
                    setIsModalOpen(true);
                    setIsMetaModalOpen(false);
                    setIsWebModalOpen(false);
                    setIsCreateMenuOpen(false);
                  }}
                >
                  Create Normal Campaign
                </button>
                <button
                  className={styles.menuItem}
                  onClick={() => {
                    setIsMetaModalOpen(true);
                    setIsModalOpen(false);
                    setIsWebModalOpen(false);
                    fetchMetaCampaigns();
                    setIsCreateMenuOpen(false);
                  }}
                >
                  Create Meta-Integrated Campaign
                </button>
                <button
                  className={styles.menuItem}
                  onClick={() => {
                    setIsWebModalOpen(true);
                    setIsModalOpen(false);
                    setIsMetaModalOpen(false);
                    setIsCreateMenuOpen(false);
                  }}
                >
                  Create Web-Based Campaign
                </button>
              </div>
            )}
          </div>
        )}
        {/* meta modal */}
        <AnimatePresence>
          {isMetaModalOpen && userRole === "vendor" && (
            <motion.div
              className={styles.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className={styles.modal}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className={styles.modalHeader}>
                  <h2>Create Meta-Integrated Campaign</h2>
                  <button
                    onClick={() => setIsMetaModalOpen(false)}
                    className={styles.closeBtn}
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleMetaSubmit} className={styles.form}>
                  <div className={styles.formGroup}>
                    <label>Meta Campaign:</label>
                    <select
                      required
                      value={selectedMetaCampaignId}
                      onChange={(e) =>
                        setSelectedMetaCampaignId(e.target.value)
                      }
                    >
                      <option value="">-- Choose a Meta campaign --</option>
                      {metaCampaigns.map((c) => (
                        <option key={c._id} value={c.campaign_id}>
                          {c.name} ({c.campaign_id}) – {c.status}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Developer:</label>
                    <select
                      required
                      value={developerId}
                      onChange={(e) => setDeveloperId(e.target.value)}
                    >
                      <option value="">Select Developer</option>
                      {developers.map((d) => (
                        <option key={d._id} value={d._id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Project:</label>
                    <select
                      required
                      disabled={!developerId}
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                    >
                      <option value="">Select Project</option>
                      {developerId &&
                        developers
                          .find((d) => d._id === developerId)
                          ?.projects?.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.projectname}
                            </option>
                          ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Initial Credit (optional):</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={initialCredit}
                      onChange={(e) => setInitialCredit(e.target.value)}
                    />
                  </div>

                  <div className={styles.formActions}>
                    <button
                      type="button"
                      onClick={() => setIsMetaModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" disabled={loading}>
                      {loading ? "Creating…" : "Connect & Create Campaign"}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* web based form model */}
        <AnimatePresence>
          {isWebModalOpen && userRole === "vendor" && (
            <motion.div
              className={styles.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className={styles.modal}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className={styles.modalHeader}>
                  <h2>Create Web-Based Campaign</h2>
                  <button
                    onClick={() => setIsWebModalOpen(false)}
                    className={styles.closeBtn}
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleWebBasedSubmit} className={styles.form}>
                  {/* campaign name */}
                  <div className={styles.formGroup}>
                    <label>Campaign Name:</label>
                    <input
                      type="text"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Developer:</label>
                    <select
                      required
                      value={developerId}
                      onChange={(e) => setDeveloperId(e.target.value)}
                    >
                      <option value="">Select Developer</option>
                      {developers.map((d) => (
                        <option key={d._id} value={d._id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Type:</label>
                    <input
                      type="text"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      required
                      placeholder="Enter campaign type (e.g. Web Based)"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Project:</label>
                    <select
                      required
                      disabled={!developerId}
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                    >
                      <option value="">Select Project</option>
                      {developerId &&
                        developers
                          .find((d) => d._id === developerId)
                          ?.projects?.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.projectname}
                            </option>
                          ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Start Date:</label>
                    <input
                      type="datetime-local"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>End Date:</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Target Segment:</label>
                    <input
                      type="text"
                      value={targetSegment}
                      onChange={(e) => setTargetSegment(e.target.value)}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Initial Credit:</label>
                    <input
                      type="number"
                      value={initialCredit}
                      onChange={(e) => setInitialCredit(e.target.value)}
                      min="0"
                    />
                  </div>

                  <div className={styles.formActions}>
                    <button
                      type="button"
                      onClick={() => setIsWebModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" disabled={loading}>
                      {loading ? "Creating..." : "Create Campaign"}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isApiModalOpen && (
            <motion.div
              className={styles.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className={styles.modal}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className={styles.modalHeader}>
                  <h2>Your Integration API</h2>
                  <button
                    onClick={() => setIsApiModalOpen(false)}
                    className={styles.closeBtn}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.apiBox}>
                  <pre className={styles.apiCode}>
                    <code>{generatedApi}</code>
                  </pre>
                  <button
                    className={styles.copyBtn}
                    onClick={() => {
                      navigator.clipboard.writeText(generatedApi);
                      toast.success("API copied to clipboard!");
                    }}
                  >
                    Copy API
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* {loading && <LoaderSpiner />} */}
      {viewType === "table" ? (
        <div className={styles.dataTableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Campaign Name</th>
                <th>Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Target Segment</th>
                <th>Project</th>
                <th>Created By</th>
                <th>Participants</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map((campaign) => (
                <tr key={campaign._id} onClick={() => handleViewData(campaign)}>
                  <td title={campaign.campaignname}>
                    {truncateText(campaign.campaignname, 30)}
                  </td>
                  <td title={campaign.type}>
                    {truncateText(campaign.type, 10)}
                  </td>
                  <td>{formatDate(campaign.startDate)}</td>
                  <td>{formatDate(campaign.endDate)}</td>
                  <td title={campaign.targetSegment}>
                    {truncateText(campaign.targetSegment, 30)}
                  </td>
                  <td title={campaign.projectId?.projectname || "N/A"}>
                    {truncateText(campaign.projectId?.projectname || "N/A", 30)}
                  </td>
                  <td title={campaign.createdBy?.email || "N/A"}>
                    {truncateText(campaign.createdBy?.email || "N/A", 30)}
                  </td>
                  <td
                    title={
                      campaign.assignedParticipants
                        ?.map((p) => p.name)
                        .join(", ") || "Not assigned yet"
                    }
                  >
                    {truncateText(
                      campaign.assignedParticipants
                        ?.map((p) => p.name)
                        .join(", ") || "Not assigned yet",
                      30
                    )}
                  </td>
                  <td title={campaign.campaignCurrentStatus}>
                    {truncateText(campaign.campaignCurrentStatus, 30)}
                  </td>
                  <td className={styles.actionButtons}>
                    {userRole === "vendor" && (
                      <>
                        <button
                          className={`${styles.toggleButton} ${campaign.campaignCurrentStatus === "enabled" ? styles.enabled : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(campaign);
                          }}
                          title={
                            campaign.campaignCurrentStatus === "enabled"
                              ? "Disable Campaign"
                              : "Enable Campaign"
                          }
                          disabled={loading}
                          aria-label={
                            campaign.campaignCurrentStatus === "enabled"
                              ? "Campaign enabled. Click to disable."
                              : "Campaign disabled. Click to enable."
                          }
                        >
                        </button>
                        <button
                          className={styles.viewButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAssignCredit(campaign._id);
                          }}
                          title="Assign Credit"
                          disabled={loading}
                        >
                          <FaCoins />
                        </button>
                        <button
                          className={styles.viewButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyCampaignId(campaign._id);
                          }}
                          title="Copy Campaign ID"
                          disabled={loading}
                        >
                          <FaCopy />
                        </button>
                        <button
                          className={styles.viewButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCampaign(campaign._id);
                          }}
                          title="Delete Campaign"
                          disabled={loading}
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.cardContainer}>
          {filteredCampaigns.map((campaign) => (
            <div key={campaign._id} className={styles.card}>
              <div className={styles.cardHeader}>
                <h3 title={campaign.campaignname}>
                  {truncateText(campaign.campaignname, 30)}
                </h3>
                {userRole === "vendor" && (
                  <div className={styles.actionButtons}>
                    <button
                      className={`${styles.toggleButton} ${campaign.campaignCurrentStatus === "enabled" ? styles.enabled : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(campaign);
                      }}
                      title={
                        campaign.campaignCurrentStatus === "enabled"
                          ? "Disable Campaign"
                          : "Enable Campaign"
                      }
                      aria-label={
                        campaign.campaignCurrentStatus === "enabled"
                          ? "Campaign enabled. Click to disable."
                          : "Campaign disabled. Click to enable."
                      }
                    >
                    </button>
                    <button
                      className={styles.viewButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenAssignCredit(campaign._id);
                      }}
                      title="Assign Credit"
                      disabled={loading}
                    >
                      <FaCoins />
                    </button>
                    <button
                      className={styles.viewButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyCampaignId(campaign._id);
                      }}
                      title="Copy Campaign ID"
                      disabled={loading}
                    >
                      <FaCopy />
                    </button>
                    <button
                      className={styles.viewButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCampaign(campaign._id);
                      }}
                      title="Delete Campaign"
                      disabled={loading}
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
              <div
                className={styles.cardBody}
                onClick={() => handleViewData(campaign)}
              >
                <p title={campaign.type}>
                  <strong>Type:</strong> {truncateText(campaign.type, 30)}
                </p>
                <p>
                  <strong>End:</strong> {formatDate(campaign.endDate)}
                </p>
                <p>
                  <strong>Credit:</strong> {campaign.creditBalance}
                </p>
                <p title={campaign.projectId?.projectname || "N/A"}>
                  <strong>Project:</strong>{" "}
                  {truncateText(campaign.projectId?.projectname || "N/A", 30)}
                </p>
                <p title={campaign.campaignCurrentStatus}>
                  <strong>Status:</strong>{" "}
                  {truncateText(campaign.campaignCurrentStatus, 30)}
                </p>
                {userRole === "vendor" && (
                  <p
                    title={
                      campaign.assignedParticipants
                        ?.map((p) => p.name)
                        .join(", ") || "Not assigned yet"
                    }
                  >
                    <strong>Participants:</strong>{" "}
                    {truncateText(
                      campaign.assignedParticipants
                        ?.map((p) => p.name)
                        .join(", ") || "Not assigned yet",
                      30
                    )}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <AnimatePresence>
        {isModalOpen && userRole === "vendor" && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={styles.modal}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className={styles.modalHeader}>
                <h2>Create Campaign</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className={styles.closeBtn}
                >
                  X
                </button>
              </div>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Campaign Name:</label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z ]*$/.test(value)) {
                        setCampaignName(e.target.value);
                      }
                    }}
                    required
                    placeholder="Enter campaign name"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Type:</label>
                  <input
                    type="text"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    required
                    placeholder="Enter campaign type"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Start Date and Time:</label>
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>End Date:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Target Segment:</label>
                  <input
                    type="text"
                    value={targetSegment}
                    onChange={(e) => setTargetSegment(e.target.value)}
                    required
                    placeholder="Enter target segment"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Developer:</label>
                  <select
                    value={developerId}
                    onChange={(e) => setDeveloperId(e.target.value)}
                    required
                  >
                    <option value="">Select Developer</option>
                    {developers.map((dev) => (
                      <option key={dev._id} value={dev._id}>
                        {dev.name || "Unknown"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Project:</label>
                  <select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    required
                    disabled={!developerId}
                  >
                    <option value="">Select Project</option>
                    {developerId &&
                      developers
                        .find((dev) => dev._id === developerId)
                        ?.projects?.map((proj) => (
                          <option key={proj._id} value={proj._id}>
                            {proj.projectname || "N/A"}
                          </option>
                        ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Upload Excel Files (.xlsx only):</label>
                  <input
                    type="file"
                    name="files"
                    accept=".xlsx"
                    multiple
                    onChange={handleFileChange}
                  />
                </div>
                <div className={styles.formActions}>
                  <button type="button" onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}>
                    {loading ? "Creating..." : "Create Campaign"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isAssignCreditModalOpen && userRole === "vendor" && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={styles.modal}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className={styles.modalHeader}>
                <h2>Assign Credit to Campaign</h2>
                {assignCreditCampaignId && (
                  <div className={styles.formGrouptext}>
                    <p>
                      <strong>*Minimum Additional Credit Needed:</strong>{" "}
                      {Math.max(
                        0,
                        (campaigns.find((c) => c._id === assignCreditCampaignId)
                          ?.sheetData?.length || 0) -
                        (parseFloat(
                          campaigns.find(
                            (c) => c._id === assignCreditCampaignId
                          )?.creditBalance
                        ) || 0)
                      )}
                      <strong>*</strong>
                    </p>
                  </div>
                )}
                <button
                  onClick={() => setIsAssignCreditModalOpen(false)}
                  className={styles.closeBtn}
                >
                  X
                </button>
              </div>
              <form onSubmit={handleAssignCreditSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Campaign:</label>
                  <p>{campaigns.find(c => c._id === assignCreditCampaignId)?.campaignname || "N/A"}</p>
                </div>
                <div className={styles.formGroup}>
                  <label>Amount:</label>
                  <input
                    type="number"
                    value={assignCreditAmount}
                    onChange={(e) => setAssignCreditAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    required
                    placeholder="Enter credit amount"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Notes:</label>
                  <input
                    type="text"
                    value={assignCreditNotes}
                    onChange={(e) => setAssignCreditNotes(e.target.value)}
                    placeholder="Enter notes (optional)"
                  />
                </div>
                <div className={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => setIsAssignCreditModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}>
                    {loading ? "Assigning..." : "Assign Credit"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isDataModalOpen && viewData.length > 0 && selectedCampaign && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={styles.dataModal}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className={styles.modalHeader}>
                <h2 title={selectedCampaign.campaignname}>
                  Dataset for {truncateText(selectedCampaign.campaignname, 30)}
                </h2>
                <button
                  onClick={() => setIsDataModalOpen(false)}
                  className={styles.closeBtn}
                >
                  X
                </button>
              </div>
              <div className={styles.dataTableWrapper}>
                <DataTable viewData={viewData} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Campaign;